/**
 * IngredientDetailSidebar Component
 *
 * Displays detailed information about a selected ingredient including:
 * - Name and category badge
 * - Default unit and recipe count
 * - Substitutes list with add/remove functionality
 * - List of recipes using this ingredient
 * - Quick stats card
 */

import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import type { IngredientWithDetails, IngredientWithCount } from '@/types/food';
import { getCategoryStyle } from '@/types/food';

interface IngredientStats {
  total: number;
  categories: number;
  withSubstitutes: number;
  unused: number;
}

interface IngredientDetailSidebarProps {
  ingredient: IngredientWithDetails | null;
  allIngredients: IngredientWithCount[];
  stats: IngredientStats;
}

export function IngredientDetailSidebar({
  ingredient,
  allIngredients,
  stats,
}: IngredientDetailSidebarProps) {
  const [substituteToAdd, setSubstituteToAdd] = useState('');
  const [isAddingSubstitute, setIsAddingSubstitute] = useState(false);
  const [isRemovingSubstitute, setIsRemovingSubstitute] = useState<number | null>(null);

  if (!ingredient) {
    return (
      <div className="flex flex-col gap-5">
        <EmptyDetailState />
        <StatsCard stats={stats} />
      </div>
    );
  }

  const categoryStyle = getCategoryStyle(ingredient.category);

  // Filter out current ingredient and its existing substitutes
  const substituteIds = new Set(ingredient.substitutes.map((s) => s.id));
  const availableSubstitutes = allIngredients.filter(
    (i) => i.id !== ingredient.id && !substituteIds.has(i.id)
  );

  const handleAddSubstitute = async () => {
    if (!substituteToAdd || isAddingSubstitute) return;

    setIsAddingSubstitute(true);
    try {
      const response = await fetch(`/food/ingredients/${ingredient.id}/add_substitute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({ substitute_id: parseInt(substituteToAdd, 10) }),
      });

      if (response.ok) {
        setSubstituteToAdd('');
        router.reload({ only: ['ingredients'] });
      }
    } finally {
      setIsAddingSubstitute(false);
    }
  };

  const handleRemoveSubstitute = async (substituteId: number) => {
    if (isRemovingSubstitute) return;

    setIsRemovingSubstitute(substituteId);
    try {
      const response = await fetch(`/food/ingredients/${ingredient.id}/remove_substitute`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({ substitute_id: substituteId }),
      });

      if (response.ok) {
        router.reload({ only: ['ingredients'] });
      }
    } finally {
      setIsRemovingSubstitute(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Detail Card */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{ingredient.name}</h2>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${categoryStyle.bg} ${categoryStyle.text}`}
          >
            {ingredient.category || 'Uncategorized'}
          </span>
        </div>

        {/* Meta Info */}
        <div className="flex flex-col gap-2 mb-5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Default Unit</span>
            <span className="font-medium text-gray-900">
              {ingredient.defaultUnit || 'Not set'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Used in</span>
            <span className="font-medium text-gray-900">
              {ingredient.recipeCount} recipe{ingredient.recipeCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Substitutes Section */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Substitutes</h3>

          {ingredient.substitutes.length > 0 ? (
            <div className="flex flex-col gap-2 mb-3">
              {ingredient.substitutes.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-lg"
                >
                  <span className="font-medium text-gray-900">{sub.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSubstitute(sub.id)}
                    disabled={isRemovingSubstitute === sub.id}
                    className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Remove substitute"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-3">No substitutes defined</p>
          )}

          {/* Add Substitute */}
          <div className="flex gap-2">
            <select
              value={substituteToAdd}
              onChange={(e) => setSubstituteToAdd(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Add substitute...</option>
              {availableSubstitutes.map((ing) => (
                <option key={ing.id} value={ing.id}>
                  {ing.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddSubstitute}
              disabled={!substituteToAdd || isAddingSubstitute}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Used In Recipes Card */}
      {ingredient.recipes && ingredient.recipes.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
            Used In Recipes
          </h3>
          <div className="flex flex-col gap-2">
            {ingredient.recipes.map((recipe) => (
              <Link
                key={recipe.id}
                href={`/food/recipes/${recipe.id}`}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-gray-900 hover:bg-blue-50 hover:text-blue-600 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                <span className="text-sm font-medium">{recipe.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Stats Card */}
      <StatsCard stats={stats} />
    </div>
  );
}

function EmptyDetailState() {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm text-center py-10">
      <div className="mb-3">
        <svg
          className="w-12 h-12 mx-auto text-gray-300"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Select an ingredient to view details</p>
    </div>
  );
}

function StatsCard({ stats }: { stats: IngredientStats }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
        Quick Stats
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-xs text-gray-500 mt-1">Total Ingredients</div>
        </div>
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{stats.categories}</div>
          <div className="text-xs text-gray-500 mt-1">Categories</div>
        </div>
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{stats.withSubstitutes}</div>
          <div className="text-xs text-gray-500 mt-1">With Substitutes</div>
        </div>
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{stats.unused}</div>
          <div className="text-xs text-gray-500 mt-1">Unused</div>
        </div>
      </div>
    </div>
  );
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') || '';
}
